import { EventCheckout } from 'store/api/event';

import { FormValues as PaymentDialogFormValues } from './payments-dialog/types';
import {
  FormValuesItems,
  AvailableRefundAmounts,
  CalculateAvailableRefundAmounts,
} from './types';

/**
 * Calculates the maximum allowed refund amounts for tip and sale total.
 *
 * @param {EventCheckout} [data] - The event checkout data.
 * @returns {{ maxAllowedTipRefund: number, maxAllowedRefund: number }} - An object containing the maximum allowed refund amounts.
 */
export const calculateMaxRefundAmounts = (data?: EventCheckout) => {
  const {
    tip = 0,
    refund = [],
    saleTotal = 0,
    processingFeesAmount = 0,
  } = data || {};

  const { alreadyRefunded, alreadyRefundedTip, alreadyRefundedProcessingFee } =
    refund.reduce(
      (acc, refundItem) => {
        refundItem.refundPayments?.forEach((payment) => {
          acc.alreadyRefunded += payment.amount || 0;
          acc.alreadyRefundedTip += Number(payment.tip ?? 0);
          acc.alreadyRefundedProcessingFee += Number(
            payment.processingFee ?? 0
          );
        });
        return acc;
      },
      {
        alreadyRefunded: 0,
        alreadyRefundedTip: 0,
        alreadyRefundedProcessingFee: 0,
      }
    );

  return {
    maxAllowedTipRefund: tip - alreadyRefundedTip,
    maxAllowedRefund: saleTotal - tip - processingFeesAmount - alreadyRefunded,
    maxAllowedProcessingFeeRefund:
      processingFeesAmount - alreadyRefundedProcessingFee,
  };
};

/**
 * Calculates the total amount of selected items.
 *
 * @param {FormValuesItems[]} items - An array of form values items.
 * @returns {number} - The total amount of selected items.
 */
export const calculateSelectedItemsAmount = (
  items: FormValuesItems[]
): number =>
  items.reduce(
    (total, item) =>
      item.selected ? total + item.price * item.quantity : total,
    0
  );

/**
 * Calculates the total tax amount that has already been refunded for an event checkout.
 *
 * @param {EventCheckout | undefined} data - The event checkout data object.
 * It should have an optional 'refund' property which is an array of refund objects.
 * Each refund object is expected to have an optional 'refundedTax' property (string or number).
 * @returns {number} The total already refunded tax amount, rounded to 2 decimal places.
 * Returns 0 if the data or refund information is missing.
 */
export const calculateAlreadyRefundedTax = (data?: EventCheckout) => {
  const alreadyRefundedTax =
    data?.refund?.reduce(
      (sum, refund) => sum + Number(refund.refundedTax || 0),
      0
    ) || 0;

  return Number(alreadyRefundedTax.toFixed(2));
};

/**
 * Calculates refund data including refund price, refundable total, and selected items amount.
 *
 * @param {object} params - An object containing parameters.
 * @param {EventCheckout} [params.data] - The event checkout data.
 * @param {FormValuesItems[]} params.items - An array of form values items.
 * @param {number} params.maxAllowedRefund - The maximum allowed refund amount.
 * @returns {{ refundPrice: number, refundableTotal: number, selectedItemsAmount: number }} - An object containing refund data.
 */
export const calculateRefundData = ({
  data,
  items,
}: {
  data?: EventCheckout;
  items: FormValuesItems[];
}): {
  refundPrice: number;
  refundableTotal: number;
  selectedItemsAmount: number;
} => {
  const refundedTax = calculateAlreadyRefundedTax(data);
  const selectedItemsAmount = calculateSelectedItemsAmount(items);

  const refundableTotal =
    selectedItemsAmount + (data?.taxAmount || 0) - refundedTax;

  return { refundableTotal, selectedItemsAmount, refundPrice: refundableTotal };
};

/**
 * Calculates the total tip and total payments from a list of payment form values.
 *
 * @param {PaymentDialogFormValues[]} [payments=[]] - An array of payment form values. Defaults to an empty array.
 * @param {string} [excludeId] - An optional ID of a payment to exclude from the calculation.
 * @returns {{ totalTip: number; totalPayments: number }} An object containing the total tip and total payments.
 */
export const calculatePaymentsAmount = (
  payments: PaymentDialogFormValues[] = [],
  excludeId?: string
): { totalTip: number; totalPayments: number; totalProcessingFee: number } =>
  payments.reduce(
    (acc, payment) => {
      if (excludeId && payment.id === excludeId) {
        return acc;
      }

      acc.totalPayments += payment.amount;
      acc.totalTip += Number(payment.tip || 0);
      acc.totalProcessingFee += Number(payment.processingFee || 0);

      return acc;
    },
    { totalTip: 0, totalPayments: 0, totalProcessingFee: 0 }
  );

/**
 * Formats a number to two decimal places.
 *
 * @param {number} num - The number to format.
 * @returns {number} The formatted number with two decimal places.
 */
export const formatNumber = (num: number): number => Number(num.toFixed(2));

/**
 * Calculates the available amounts for a refund based on the checkout data, selected items,
 * existing return payments, and maximum allowed tip refund.
 *
 * @param {CalculateAvailableRefundAmounts} params - An object containing the parameters for the calculation.
 * @param {EventCheckout | undefined} params.data - The event checkout data.
 * @param {FormValuesItems[]} params.items - The list of items being refunded.
 * @param {string | undefined} params.excludeId - The ID of a return payment to exclude from the calculation.
 * @param {ReturnPayment[]} params.returnPayments - The list of existing return payments.
 * @param {number | undefined} params.maxAllowedTipRefund - The maximum allowed amount for tip refund.
 * @param {number | undefined} params.maxAllowedProcessingFeeRefund - The maximum allowed amount for processing fee refund.
 * @returns {AvailableRefundAmounts} An object containing the total refundable amount, remaining amount,
 * remaining tip amount, and the amount of the selected items.
 */
export const calculateAvailableRefundAmounts = ({
  data,
  items,
  excludeId,
  returnPayments,
  maxAllowedTipRefund,
  maxAllowedProcessingFeeRefund,
}: CalculateAvailableRefundAmounts): AvailableRefundAmounts => {
  // Calculate how much tax has already been refunded
  const alreadyRefundedTax = calculateAlreadyRefundedTax(data);

  // Sum of selected items
  const selectedItemsAmount = calculateSelectedItemsAmount(items);

  // Total amount of tax from the cheque
  const tax = Number(data?.taxAmount || 0);

  // Available tax to be refunded
  const availableTax = tax - alreadyRefundedTax;

  // Maximum refundable amount = sum of selected goods + remaining tax
  const totalRefundable = formatNumber(selectedItemsAmount + availableTax);

  // Calculate the sum of already entered payments, tips and processing fee
  const { totalTip, totalPayments, totalProcessingFee } =
    calculatePaymentsAmount(returnPayments, excludeId);

  // Balance by amount of payments
  const remainingAmount = formatNumber(totalRefundable - totalPayments);

  // Tip balance
  const remainingTipAmount = formatNumber(
    (maxAllowedTipRefund ?? 0) - totalTip
  );

  // Processing fee balance
  const remainingProcessingFeeAmount = formatNumber(
    (maxAllowedProcessingFeeRefund ?? 0) - totalProcessingFee
  );

  return {
    totalRefundable,
    remainingAmount,
    remainingTipAmount,
    selectedItemsAmount,
    remainingProcessingFeeAmount,
  };
};
